import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { forumApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { formatApiError } from '../lib/api';
import { MessageSquare, Eye, Pin, Lock, Plus, Wrench, Flag, Trophy, Car, Users, ImagePlus, X, Trash2, Medal } from 'lucide-react';

const categoryIcons = {
  general: Users,
  tech: Wrench,
  tracks: Flag,
  results: Trophy,
  builds: Car,
  'race-wins': Medal,
};

export default function ForumPage() {
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  
  const [categories, setCategories] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(categoryFilter || null);
  const [isNewThreadOpen, setIsNewThreadOpen] = useState(false);
  const [newThread, setNewThread] = useState({ category_id: '', title: '', content: '' });
  const [threadImages, setThreadImages] = useState([]);
  const [threadUploading, setThreadUploading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const threadFileRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, threadRes] = await Promise.all([
          forumApi.getCategories(),
          forumApi.getThreads(selectedCategory, 0, 50),
        ]);
        setCategories(catRes.data);
        setThreads(threadRes.data);
      } catch (error) {
        console.error('Error fetching forum data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  const handleCategorySelect = (catId) => {
    setSelectedCategory(catId === selectedCategory ? null : catId);
  };

  const handleThreadImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setThreadUploading(true);
    try {
      for (const file of files) {
        const res = await forumApi.uploadImage(file);
        setThreadImages(prev => [...prev, res.data.image_url]);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setThreadUploading(false);
      if (threadFileRef.current) threadFileRef.current.value = '';
    }
  };

  const handleDeleteThread = async (e, threadId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this thread and all its replies?')) return;
    try {
      await forumApi.deleteThread(threadId);
      setThreads(threads.filter(t => t.id !== threadId));
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!newThread.category_id || !newThread.title || !newThread.content) {
      setError('Please fill in all fields');
      return;
    }

    try {
      let fullContent = newThread.content;
      if (threadImages.length > 0) {
        fullContent += '\n\n' + threadImages.map(url => `[image]${url}[/image]`).join('\n');
      }
      await forumApi.createThread({ ...newThread, content: fullContent });
      setIsNewThreadOpen(false);
      setNewThread({ category_id: '', title: '', content: '' });
      setThreadImages([]);
      const threadRes = await forumApi.getThreads(selectedCategory, 0, 50);
      setThreads(threadRes.data);
    } catch (error) {
      setError(formatApiError(error));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-400 tag">Loading forum...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-between">
            <div>
              <p className="tag text-[#FF3B30] mb-2">Community</p>
              <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">Forum</h1>
              <p className="text-gray-400 max-w-2xl">
                Connect with fellow Crown Vic racers. Share tips, discuss builds, and find help from the community.
              </p>
            </div>
            {user && (
              <Dialog open={isNewThreadOpen} onOpenChange={setIsNewThreadOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FF3B30] hover:bg-[#E6352B] text-white" data-testid="new-thread-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Thread
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border-[#262626] text-white">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Create New Thread</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateThread} className="space-y-4">
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div>
                      <Label>Category</Label>
                      <select
                        value={newThread.category_id}
                        onChange={(e) => setNewThread({ ...newThread, category_id: e.target.value })}
                        className="w-full mt-1 p-2 bg-[#1F1F1F] border border-[#262626] rounded text-white focus:border-[#FF3B30] focus:outline-none"
                        data-testid="thread-category-select"
                      >
                        <option value="">Select a category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={newThread.title}
                        onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
                        placeholder="Thread title"
                        className="bg-[#1F1F1F] border-[#262626] text-white focus:border-[#FF3B30]"
                        data-testid="thread-title-input"
                      />
                    </div>
                    <div>
                      <Label>Content</Label>
                      <Textarea
                        value={newThread.content}
                        onChange={(e) => setNewThread({ ...newThread, content: e.target.value })}
                        placeholder="What's on your mind?"
                        rows={6}
                        className="bg-[#1F1F1F] border-[#262626] text-white focus:border-[#FF3B30]"
                        data-testid="thread-content-input"
                      />
                    </div>
                    {/* Image Upload */}
                    <div>
                      <Label>Photos (optional)</Label>
                      <input
                        type="file"
                        ref={threadFileRef}
                        onChange={handleThreadImageUpload}
                        accept="image/*"
                        multiple
                        className="hidden"
                      />
                      {threadImages.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2 mt-2">
                          {threadImages.map((url, idx) => (
                            <div key={idx} className="relative group">
                              <img src={url} alt="" className="w-20 h-20 object-cover rounded border border-[#262626]" />
                              <button
                                type="button"
                                onClick={() => setThreadImages(prev => prev.filter((_, i) => i !== idx))}
                                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => threadFileRef.current?.click()}
                        disabled={threadUploading}
                        className="mt-1 border-[#262626] text-gray-300 hover:text-white"
                        data-testid="thread-attach-image-btn"
                      >
                        <ImagePlus className="w-4 h-4 mr-2" />
                        {threadUploading ? 'Uploading...' : 'Add Photos'}
                      </Button>
                    </div>
                    <Button type="submit" className="w-full bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="submit-thread-btn">
                      Create Thread
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <h3 className="tag text-gray-400 mb-4">Categories</h3>
            <div className="space-y-2">
              <button
                onClick={() => handleCategorySelect(null)}
                className={`w-full text-left p-3 rounded transition-colors ${
                  !selectedCategory ? 'bg-[#FF3B30] text-white' : 'bg-[#141414] text-gray-300 hover:bg-[#1F1F1F]'
                }`}
                data-testid="category-all"
              >
                <span className="font-medium">All Discussions</span>
              </button>
              {categories.map((cat) => {
                const IconComponent = categoryIcons[cat.id] || MessageSquare;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.id)}
                    className={`w-full text-left p-3 rounded transition-colors ${
                      selectedCategory === cat.id ? 'bg-[#FF3B30] text-white' : 'bg-[#141414] text-gray-300 hover:bg-[#1F1F1F]'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <IconComponent className="w-5 h-5" />
                      <div>
                        <span className="font-medium block">{cat.name}</span>
                        <span className="text-xs opacity-70">{cat.thread_count} threads</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Threads List */}
          <div className="lg:col-span-3">
            <div className="bg-[#141414] rounded border border-[#262626]">
              {/* Header */}
              <div className="p-4 border-b border-[#262626] flex items-center justify-between">
                <h3 className="font-heading font-bold text-white">
                  {selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : 'All Discussions'}
                </h3>
                <span className="text-gray-500 text-sm">{threads.length} threads</span>
              </div>

              {/* Thread List */}
              <div className="divide-y divide-[#262626]">
                {threads.map((thread) => (
                  <Link 
                    key={thread.id} 
                    to={`/forum/thread/${thread.id}`}
                    className="block p-4 hover:bg-[#1F1F1F] transition-colors"
                    data-testid={`forum-thread-${thread.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {thread.is_pinned && (
                            <Pin className="w-4 h-4 text-[#FF3B30]" />
                          )}
                          {thread.is_locked && (
                            <Lock className="w-4 h-4 text-gray-500" />
                          )}
                          <h4 className="font-medium text-white truncate">{thread.title}</h4>
                        </div>
                        <p className="text-gray-500 text-sm">
                          by {thread.author_id ? (
                            <Link to={`/profile/${thread.author_id}`} onClick={(e) => e.stopPropagation()} className="text-gray-300 hover:text-[#FF3B30] transition-colors">
                              {thread.author_name}
                            </Link>
                          ) : (
                            <span className="text-gray-400">{thread.author_name}</span>
                          )} • {formatDate(thread.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-gray-500 flex-shrink-0">
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          <span>{thread.reply_count}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          <span>{thread.view_count}</span>
                        </div>
                        <div className="hidden md:block text-right min-w-[100px]">
                          {thread.last_reply_at ? (
                            <>
                              <p className="text-xs">{formatDate(thread.last_reply_at)}</p>
                              <p className="text-xs text-gray-600">by {thread.last_reply_by}</p>
                            </>
                          ) : (
                            <p className="text-xs">No replies yet</p>
                          )}
                        </div>
                        {user && user.role === 'admin' && (
                          <button
                            onClick={(e) => handleDeleteThread(e, thread.id)}
                            className="text-gray-600 hover:text-red-400 transition-colors ml-2"
                            data-testid={`delete-thread-${thread.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}

                {threads.length === 0 && (
                  <div className="p-12 text-center">
                    <MessageSquare className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400 mb-4">No threads yet in this category.</p>
                    {user ? (
                      <Button 
                        onClick={() => setIsNewThreadOpen(true)}
                        className="bg-[#FF3B30] hover:bg-[#E6352B]"
                      >
                        Start the first thread
                      </Button>
                    ) : (
                      <Link to="/login">
                        <Button className="bg-[#FF3B30] hover:bg-[#E6352B]">
                          Login to post
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
